/**
 * offline-banner.component.ts
 *
 * app-offline-banner
 *
 * The app's answer to "why is nothing working": a single bar that appears when the
 * device loses its connection and goes away when it comes back. Mounted once in
 * the app shell (app.component.html), so it covers every route without any page
 * having to know about it.
 *
 * A banner rather than a blocking overlay, deliberately. Nothing in this app works
 * offline, but plenty of it is worth *looking* at — photos already taken, values
 * already typed, the review screen someone is halfway through — and an overlay
 * would lock a receiver out of their own half-finished log entry while they walk
 * back toward signal. So this states the problem where it cannot be missed and
 * leaves the app reachable underneath.
 *
 * It reports state and does not repair it: no request is retried, held or replayed
 * when the connection returns. See {@link ConnectivityService} for why that is a
 * decision rather than an omission.
 *
 * Styling uses the global theme tokens from src/theme/variables.css (warning
 * amber on graphite, matching the app's other "you need to deal with this"
 * messaging) and overrides no global rule.
 */

import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ConnectivityService } from '../../../service/connectivity.service';

@Component({
  selector: 'app-offline-banner',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './offline-banner.component.html',
  styleUrls: ['./offline-banner.component.css'],
})
export class OfflineBannerComponent implements OnInit, OnDestroy {
  // #region Fields
  private readonly _connectivity = inject(ConnectivityService);

  private _connectivitySubscription?: Subscription;

  /** Clears the "still offline" note after {@link _retryNoteMs}. */
  private _retryNoteTimer?: ReturnType<typeof setTimeout>;

  /** How long the failed-retry note stays up before the banner reads plainly again. */
  private readonly _retryNoteMs = 4000;

  /** Whether the banner is currently in the DOM. */
  public isVisible = false;

  /**
   * True while a "Retry" that found nothing is being acknowledged. Kept separate
   * from {@link isVisible} so the tap gets visible feedback — without it, retrying
   * in a dead spot looks like a button that does nothing.
   */
  public retryFoundNothing = false;
  // #endregion

  // #region Lifecycle
  /**
   * Subscribes to connectivity and shows or hides the banner accordingly.
   *
   * Dismissal is scoped to one outage: coming back online clears it, so the next
   * drop is announced again. A receiver who waves the bar away in the morning
   * should still be told about the dead spot they walk into after lunch.
   */
  public ngOnInit(): void {
    this._connectivitySubscription = this._connectivity.isOnline$.subscribe(
      (isOnline: boolean) => {
        this.isVisible = !isOnline;
        if (isOnline) {
          this.clearRetryNote();
        }
      }
    );
  }

  /** Releases the connectivity subscription and any pending retry-note timer. */
  public ngOnDestroy(): void {
    this._connectivitySubscription?.unsubscribe();
    this.clearRetryNote();
  }
  // #endregion

  // #region User Actions
  /**
   * Re-checks the connection. On success the service's own emission hides the
   * banner, so there is nothing to do here; on failure the banner says so for a
   * few seconds rather than sitting silent.
   */
  public onRetry(): void {
    if (this._connectivity.recheck()) {
      return;
    }

    this.clearRetryNote();
    this.retryFoundNothing = true;
    this._retryNoteTimer = setTimeout(() => {
      this.retryFoundNothing = false;
    }, this._retryNoteMs);
  }

  /**
   * Hides the banner for the rest of this outage.
   *
   * Offered because the message has a shelf life — after the first read it is just
   * a bar taking up room on a phone screen, and someone deliberately working
   * through what they can see does not need it repeated. Reconnecting resets this
   * (see {@link ngOnInit}).
   */
  public onDismiss(): void {
    this.isVisible = false;
    this.clearRetryNote();
  }
  // #endregion

  // #region Internals
  /** Drops the failed-retry note and its pending timer. */
  private clearRetryNote(): void {
    if (this._retryNoteTimer) {
      clearTimeout(this._retryNoteTimer);
      this._retryNoteTimer = undefined;
    }
    this.retryFoundNothing = false;
  }
  // #endregion
}
